import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig'; // Adjust import path as needed
import Logo from '../../assets/icons/CampusLLMLogoSmall.png';
import GoogleSignInButton from "../../components/specific/GoogleSignInButton";
import bgLines from "../../assets/images/bg-line1.svg"

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const { loginWithEmail, login, signup, currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [isOnPhone, setIsOnPhone] = useState();
  const [emailSent, setEmailSent] = useState(false);
  const [isCheckingUniversity, setIsCheckingUniversity] = useState(false);
  const [loginMethod, setLoginMethod] = useState("password"); // "password" or "email"

  useEffect(() => {
    // Function to detect if user is on a phone
    const detectPhone = () => {
      // Method 1: Screen size detection
      const isMobileBySize = window.innerWidth < 550;

      // Method 2: User agent detection (not always reliable but helpful as a secondary check)
      const isMobileByAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

      // Method 3: Touch capability detection (most phones have touch screens)
      const isMobileByTouch = 'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0;

      // Method 4: Device orientation capability (most phones have this)
      const hasOrientationSupport = Boolean(window.DeviceOrientationEvent);

      // Combine signals to make a decision
      // More conservative approach: require screen size AND at least one other indicator
      const isPhone = isMobileBySize && (isMobileByAgent || isMobileByTouch || hasOrientationSupport);

      setIsOnPhone(isPhone);
    };

    // Set initial value on component mount
    detectPhone();

    // Add event listener to detect orientation or size changes
    window.addEventListener('resize', detectPhone);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('resize', detectPhone);
    };
  }, []);

  // Extract university domain from email
  const getUniversityDomain = (email) => {
    const domain = email.split('@')[1];
    return domain;
  };

  // Check if university exists in colleges collection
  const checkUniversityExists = async (domain) => {
    try {
      const collegeDoc = await getDoc(doc(db, 'colleges', domain));
      return collegeDoc.exists();
    } catch (error) {
      console.error("Error checking university:", error);
      return false;
    }
  };

  // Check if current user has a valid university email and university is configured
  useEffect(() => {
    const checkUserAndUniversity = async () => {
      if (currentUser && currentUser.email) {
        setIsCheckingUniversity(true);

        const domain = getUniversityDomain(currentUser.email);

        // Check if it's a university email (ends with .edu)
        if (!domain.endsWith('.edu')) {
          try {
            await logout();
            setError("Only university email addresses (.edu) are allowed.");
          } catch (error) {
            console.error("Logout error:", error);
          }
          setIsCheckingUniversity(false);
          return;
        }

        // Check if university is configured in Firebase
        const universityExists = await checkUniversityExists(domain);

        if (!universityExists) {
          // University not configured, redirect to setup
          navigate("/setup");
        } else {
          // Valid university user, navigate to home
          navigate("/");
        }

        setIsCheckingUniversity(false);
      }
    };

    checkUserAndUniversity();
  }, [currentUser, logout, navigate]);

  const handlePasswordAuth = async (e) => {
    e.preventDefault();
    setError("");

    // Validate email domain is .edu
    const domain = getUniversityDomain(email);
    if (!domain.endsWith('.edu')) {
      setError("Only university email addresses (.edu) are allowed.");
      return;
    }

    // Validate password requirements
    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    // If signing up, check password confirmation
    if (isSignUp && password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      if (isSignUp) {
        await signup(email, password);
      } else {
        await login(email, password);
      }
    } catch (error) {
      console.error("Authentication error:", error);

      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/user-not-found':
          setError("No account found with this email. Please sign up first.");
          break;
        case 'auth/wrong-password':
          setError("Incorrect password. Please try again.");
          break;
        case 'auth/email-already-in-use':
          setError("An account with this email already exists. Please sign in instead.");
          break;
        case 'auth/weak-password':
          setError("Password is too weak. Please choose a stronger password.");
          break;
        case 'auth/invalid-email':
          setError("Invalid email address format.");
          break;
        case 'auth/too-many-requests':
          setError("Too many failed attempts. Please try again later.");
          break;
        default:
          setError(error.message || "Authentication failed. Please try again.");
      }
    }
  };

  const handleEmailContinue = async (e) => {
    e.preventDefault();
    setError("");

    // Validate email domain is .edu
    const domain = getUniversityDomain(email);
    if (!domain.endsWith('.edu')) {
      setError("Only university email addresses (.edu) are allowed.");
      return;
    }

    try {
      setEmailSent(true);
      await loginWithEmail(email);
    } catch (error) {
      setError("Failed to continue with email: " + (error.message || ""));
      setEmailSent(false);
      console.error("Login error:", error);
    }
  };

  // Show loading state while checking university
  if (isCheckingUniversity) {
    return (
      <main className={`w-screen h-screen flex justify-center items-center relative ${isOnPhone ? "bg-black" : "bg-[#1e1e1e]"}`}>
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Checking university configuration...</p>
        </div>
      </main>
    );
  }

  return (
    <main className={`w-screen h-screen flex justify-center items-center relative ${isOnPhone ? "bg-black" : "bg-[#1e1e1e]"}`}>
      <section className={`w-full h-full flex shadow-lg overflow-hidden ${isOnPhone ? "bg-black" : "bg-[#1e1e1e]"}`}>
        <div className={`w-full md:w-3/5 ${isOnPhone ? "" : "border-r border-[#2e2e2e]"} flex flex-col p-8`}>
          <div className="flex items-center mb-8">
            <a className="flex items-center space-x-2" href="/">
              <img className="h-12 p-1 bg-[#1e1e1e] border border-[#2e2e2e] rounded-lg" src={Logo} alt="Logo" />
              <div className={"font-Poppins font-semibold text-3xl text-white"}>CampusLM</div>
            </a>
          </div>
          <div className="flex flex-col items-center justify-center flex-grow">
            <h1 className="text-4xl font-semibold mb-4 text-white">
              {isSignUp ? "Create Account" : "Welcome Back"}
            </h1>
            <p className="text-lg text-[#afafaf] mb-8">
              {isSignUp
                ? "Create your CampusLM account to get started."
                : "Enter your details to log into your CampusLM account."
              }
            </p>

            {/* Sign in buttons */}
            <div className="w-full max-w-md space-y-4 mb-4">
              <GoogleSignInButton />
            </div>

            <div className={`h-0.5 w-full ${isOnPhone ? "bg-[#1e1e1e]" : "bg-[#2e2e2e]"} rounded-full max-w-lg mb-4`} />

            {/* Login method toggle */}
            <div className="w-full max-w-md mb-4">
              <div className="flex bg-[#2e2e2e] rounded-lg p-1">
                <button
                  onClick={() => setLoginMethod("password")}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    loginMethod === "password"
                      ? "bg-primary text-white"
                      : "text-[#afafaf] hover:text-white"
                  }`}
                >
                  Password
                </button>
                <button
                  onClick={() => setLoginMethod("email")}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    loginMethod === "email"
                      ? "bg-primary text-white"
                      : "text-[#afafaf] hover:text-white"
                  }`}
                >
                  Email Link
                </button>
              </div>
            </div>

            {/* Display success or error message */}
            {emailSent && loginMethod === "email" && (
              <div className="w-full max-w-md p-3 mb-4 bg-green-500 bg-opacity-20 border border-green-500 text-green-100 rounded-lg text-center">
                Sign-in link sent to your email. Please check your inbox and spam folder. Please be patient.
              </div>
            )}
            {error && (
              <div className="w-full max-w-md p-3 mb-4 bg-red-500 bg-opacity-20 border border-red-500 text-red-100 rounded-lg text-center">
                {error}
              </div>
            )}

            {/* Password authentication form */}
            {loginMethod === "password" && (
              <form className="w-full max-w-md" onSubmit={handlePasswordAuth}>
                <input
                  className="w-full p-4 mb-4 bg-[#1e1e1e] border-[#3e3e3e] border text-[#afafaf] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (university.edu only)"
                  required
                />
                <div className="relative mb-4">
                  <input
                    className="w-full p-4 bg-[#1e1e1e] border-[#3e3e3e] border text-[#afafaf] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary pr-12"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password (min. 6 characters)"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#afafaf] hover:text-white"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>

                {/* Confirm password field for sign up */}
                {isSignUp && (
                  <input
                    className="w-full p-4 mb-4 bg-[#1e1e1e] border-[#3e3e3e] border text-[#afafaf] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm Password"
                    required
                  />
                )}

                <button
                  className="w-full py-4 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition duration-300"
                  type="submit"
                >
                  {isSignUp ? "Create Account" : "Sign In"}
                </button>

                {/* Toggle between sign in and sign up */}
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsSignUp(!isSignUp);
                      setError("");
                      setPassword("");
                      setConfirmPassword("");
                    }}
                    className="text-[#afafaf] hover:text-white text-sm underline"
                  >
                    {isSignUp
                      ? "Already have an account? Sign in"
                      : "Don't have an account? Sign up"
                    }
                  </button>
                </div>

                <p className="text-xs text-center mt-2 text-[#afafaf]">
                  Note: Only university email addresses (.edu) are allowed
                </p>
              </form>
            )}

            {/* Email link authentication form */}
            {loginMethod === "email" && (
              <form className="w-full max-w-md" onSubmit={handleEmailContinue}>
                <input
                  className="w-full p-4 mb-4 bg-[#1e1e1e] border-[#3e3e3e] border text-[#afafaf] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (university.edu only)"
                  required
                />
                <button
                  className="w-full py-4 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition duration-300"
                  type="submit"
                >
                  Continue with Email
                </button>
                <p className="text-xs text-center mt-2 text-[#afafaf]">
                  Note: Only university email addresses (.edu) are allowed
                </p>
              </form>
            )}
          </div>
        </div>
        <div className="items-center justify-center relative overflow-clip w-0 hidden md:flex md:w-2/5 bg-black text-white p-8 flex-col h-full">
          <div className={"absolute h-full w-screen rotate-45 -mt-96 opacity-50"}>
            <img src={bgLines} alt="Background" />
          </div>
          <div className="absolute w-40 h-40 p-4 rounded-xl shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-[#B0276E] via-[#F07458] to-[#FFEEA6] rounded-xl opacity-100"></div>
            <div className="absolute inset-0.5 backdrop-filter backdrop-blur-md bg-[#1e1e1e] rounded-xl"></div>
            <div className="relative">
              <img src={Logo} className={""} alt="Logo" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Login;